import { useState, useEffect } from 'preact/hooks';
import { MainLayout } from '../../components/MainLayout';
import { toast } from '../../services/toast.service';
import { getAuthHeaders } from '../../utils/auth';

interface Invitation {
  invitationId: string;
  teamId: string;
  email: string;
  role: 'admin' | 'member';
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  token: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
}

export function InvitationsPage() {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/invitations`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load invitations');
      }

      const data = await response.json();
      setInvitations(data.invitations || []);
    } catch (err) {
      console.error('Error loading invitations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitation: Invitation) => {
    setActionLoading(invitation.invitationId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/invitations/${invitation.invitationId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          token: invitation.token,
          teamId: invitation.teamId,
          email: invitation.email
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to accept invitation');
      }

      // Remove accepted invitation from list and redirect to team
      setInvitations(prev => prev.filter(inv => inv.invitationId !== invitation.invitationId));

      // Show success and redirect to team page
      toast.success(`Successfully joined the team!`);
      window.location.href = `/teams/${invitation.teamId}`;
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (invitation: Invitation) => {
    if (!confirm(`Decline invitation to join this team?`)) {
      return;
    }

    setActionLoading(invitation.invitationId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/invitations/${invitation.invitationId}/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({
          token: invitation.token,
          teamId: invitation.teamId,
          email: invitation.email
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to decline invitation');
      }

      // Remove declined invitation from list
      setInvitations(prev => prev.filter(inv => inv.invitationId !== invitation.invitationId));
    } catch (err) {
      console.error('Error declining invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to decline invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isExpiring = (expiresAt: string) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysLeft <= 3;
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex items-center justify-center h-full">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
        <div class="container mx-auto p-8 max-w-4xl">
        {/* Header */}
        <div class="flex items-center gap-4 mb-8">
          <div>
            <h1 class="text-3xl font-bold">Team Invitations</h1>
            <p class="text-base-content/70 mt-2">
              Review and respond to team invitations
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div class="alert alert-error mb-6">
            <span>{error}</span>
          </div>
        )}

        {/* Invitations List */}
        {invitations.length === 0 ? (
          <div class="card bg-base-100 shadow-md">
            <div class="card-body text-center py-12">
              <div class="text-6xl mb-4">📬</div>
              <h2 class="text-xl font-semibold mb-2">No pending invitations</h2>
              <p class="text-base-content/70">
                You don't have any team invitations at the moment.
              </p>
              <div class="mt-6">
                <a href="/teams" class="btn btn-primary">Browse Teams</a>
              </div>
            </div>
          </div>
        ) : (
          <div class="space-y-4">
            {invitations.map((invitation) => (
              <div key={invitation.invitationId} class="card bg-base-100 shadow-md">
                <div class="card-body">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <h3 class="text-lg font-semibold mb-2">Team Invitation</h3>
                      <p class="text-base-content/70 mb-4">
                        You've been invited to join as a{' '}
                        <span class="badge badge-outline capitalize">{invitation.role}</span>
                      </p>

                      <div class="flex items-center gap-4 text-sm text-base-content/60">
                        <span>Invited: {formatDate(invitation.createdAt)}</span>
                        <span class={`${isExpiring(invitation.expiresAt) ? 'text-warning' : ''}`}>
                          Expires: {formatDate(invitation.expiresAt)}
                          {isExpiring(invitation.expiresAt) && (
                            <span class="ml-1">⚠️ Expiring soon</span>
                          )}
                        </span>
                      </div>
                    </div>

                    <div class="flex gap-2">
                      <button
                        onClick={() => handleAccept(invitation)}
                        disabled={actionLoading === invitation.invitationId}
                        class="btn btn-primary btn-sm"
                      >
                        {actionLoading === invitation.invitationId ? (
                          <span class="loading loading-spinner loading-xs"></span>
                        ) : (
                          'Accept'
                        )}
                      </button>
                      <button
                        onClick={() => handleDecline(invitation)}
                        disabled={actionLoading === invitation.invitationId}
                        class="btn btn-ghost btn-sm"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </MainLayout>
  );
}
